<template>
  <div class="redeem-page">
    <!-- Admin nav toggle -->
    <div v-if="isAdminUser && !isDrawerVisible" class="admin-nav-toggle">
      <v-btn
        small
        flat
        color="grey darken-1"
        class="admin-toggle-btn"
        @click="showNavigation"
      >
        <v-icon small class="mr-1">mdi-menu</v-icon>
        Show navigation
      </v-btn>
    </div>

    <div class="redeem-container">
      <!-- Header -->
      <div class="redeem-header">
        <v-icon class="redeem-header-icon" color="primary">mdi-ticket</v-icon>
        <div class="redeem-header-text">
          <div class="redeem-title">Camp Coupon Redemption</div>
          <div class="redeem-subtitle">
            Enter a coupon number to redeem
          </div>
        </div>
      </div>

      <!-- Coupon lookup -->
      <v-card class="redeem-card lookup-card" elevation="2">
        <v-card-text class="px-4 py-4">
          <div class="lookup-label">Coupon Number</div>
          <v-layout row align-center class="mt-2">
            <v-flex>
              <v-text-field
                v-model="couponIdInput"
                placeholder="Enter coupon number..."
                outline
                single-line
                hide-details
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                class="coupon-input"
                @keyup.enter="loadCoupon"
                @input="onCouponInput"
                @keypress="allowDigitsOnly"
                @paste="onCouponPaste"
                :disabled="loadingInfo"
              />
            </v-flex>
            <v-flex shrink class="ml-3">
              <v-btn
                color="success"
                :loading="loadingInfo"
                :disabled="loadingInfo"
                @click="loadCoupon"
                class="load-btn"
              >
                <v-icon small class="mr-1">mdi-magnify</v-icon>
                Find Coupon
              </v-btn>
            </v-flex>
          </v-layout>
          <div v-if="couponInputError" class="coupon-input-error mt-1">
            {{ couponInputError }}
          </div>
        </v-card-text>
      </v-card>

      <!-- Error state -->
      <v-alert
        :value="!!lookupError"
        type="error"
        class="mt-3"
        @input="lookupError = ''"
      >
        {{ lookupError }}
      </v-alert>

      <!-- Coupon info -->
      <template v-if="couponInfo">
        <!-- Already redeemed banner -->
        <v-alert
          :value="!!couponInfo.coupon.redeemedAt"
          type="warning"
          class="mt-3"
        >
          <strong>Already redeemed</strong> on
          {{ formatDateTime(couponInfo.coupon.redeemedAt) }}
          by {{ couponInfo.coupon.redeemedBy || "unknown" }} — Amount:
          {{ formatCurrency(couponInfo.coupon.amountRedeemed) }}
        </v-alert>

        <div class="info-cards mt-3">
          <!-- Account info card -->
          <v-card class="redeem-card info-card" elevation="2">
            <v-card-text class="px-4 py-3">
              <div class="info-card-label">
                <v-icon small class="mr-1" color="grey darken-1"
                  >mdi-account</v-icon
                >
                Account
              </div>
              <div class="info-name mt-2">{{ couponInfo.account.name }}</div>
              <div v-if="couponInfo.account.nameH" class="info-name-h">
                {{ couponInfo.account.nameH }}
              </div>
              <div class="info-detail mt-1">
                <v-icon x-small class="mr-1" color="grey"
                  >mdi-identifier</v-icon
                >
                Account #{{ couponInfo.account.accountID }}
              </div>
              <div v-if="couponInfo.account.address1" class="info-detail">
                <v-icon x-small class="mr-1" color="grey"
                  >mdi-map-marker-outline</v-icon
                >
                {{ couponInfo.account.address1 }}
              </div>
            </v-card-text>
          </v-card>

          <!-- Coupon info card -->
          <v-card class="redeem-card info-card" elevation="2">
            <v-card-text class="px-4 py-3">
              <div class="info-card-label">
                <v-icon small class="mr-1" color="grey darken-1"
                  >mdi-ticket</v-icon
                >
                Coupon Details
              </div>
              <div class="coupon-detail-grid mt-2">
                <div>
                  <div class="detail-label">Coupon #</div>
                  <div class="detail-value">{{ couponInfo.coupon.id }}</div>
                </div>
                <div>
                  <div class="detail-label">Amount</div>
                  <div class="detail-value amount-value">
                    {{ formatCurrency(couponInfo.coupon.amount) }}
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Redeem action -->
        <v-card
          v-if="!couponInfo.coupon.redeemedAt"
          class="redeem-card redeem-action-card mt-3"
          elevation="2"
        >
          <v-card-text class="px-4 py-4">
            <v-layout row wrap align-center>
              <v-flex xs12 sm8>
                <div class="redeem-action-label">Redemption Amount</div>
                <div class="redeem-amount-display">
                  {{ formatCurrency(couponInfo.coupon.amount) }}
                </div>
              </v-flex>
              <v-flex xs12 sm4 class="text-sm-right mt-3 mt-sm-0 mr-3">
                <v-btn
                  color="success"
                  large
                  :loading="redeeming"
                  :disabled="redeeming"
                  class="redeem-btn mb-0"
                  @click="confirmRedeem"
                >
                  <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
                  Redeem Coupon
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </template>

      <!-- Coupons redeemed section -->
      <div class="redeemed-section mt-4">
        <div class="redeemed-section-header">
          <div class="redeemed-section-left">
            <span class="redeemed-section-title">Coupons Redeemed By User</span>
            <span
              v-if="redeemedCoupons.length"
              class="redeemed-section-meta ml-2"
            >
              {{ redeemedCoupons.length }} ·
              <strong>{{ formatCurrency(redeemedTotal) }}</strong>
            </span>
          </div>
          <v-btn
            flat
            small
            color="grey"
            class="redeemed-refresh-btn"
            :loading="loadingRedeemed"
            @click="loadRedeemedByUser"
          >
            <v-icon small class="mr-1">mdi-reload</v-icon>
            Refresh
          </v-btn>
        </div>

        <div v-if="loadingRedeemed" class="redeemed-loading">
          <v-progress-circular indeterminate color="warning" size="28" />
        </div>

        <div v-else-if="redeemedError" class="redeemed-error">
          <v-icon small color="error" class="mr-1"
            >mdi-alert-circle-outline</v-icon
          >
          {{ redeemedError }}
        </div>

        <div v-else-if="!redeemedCoupons.length" class="redeemed-empty">
          No coupons redeemed yet.
        </div>

        <div v-else>
          <v-text-field
            v-model="redeemedSearch"
            placeholder="Search by name, account, address..."
            solo
            flat
            single-line
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            class="redeemed-search mb-3"
          />

          <div v-if="!filteredRedeemedCoupons.length" class="redeemed-empty">
            No results for "{{ redeemedSearch }}".
          </div>

          <div
            v-for="(coupon, index) in filteredRedeemedCoupons"
            :key="coupon.id || index"
            class="redeemed-row"
          >
            <!-- Top: coupon chip + amount -->
            <div class="redeemed-row-header">
              <div class="redeemed-id-chip">#{{ coupon.id }}</div>
              <div class="redeemed-amounts">
                <div class="redeemed-amt-block redeemed-orange">
                  <div class="redeemed-amt-label">Redeemed</div>
                  <div class="redeemed-amt-val">
                    {{ formatCurrency(coupon.amountRedeemed) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Account name block -->
            <div class="redeemed-names mt-2">
              <span class="redeemed-account-id-plain">{{
                coupon.accountID
              }}</span>
              <span
                v-if="coupon.accountName"
                class="redeemed-account-name ml-1"
                >{{ coupon.accountName }}</span
              >
              <span v-if="coupon.accountNameH" class="redeemed-name-h ml-2">{{
                coupon.accountNameH
              }}</span>
            </div>

            <!-- Meta row: address + date -->
            <div class="redeemed-meta-row mt-1">
              <span v-if="coupon.address1" class="redeemed-meta-item">
                <v-icon x-small class="mr-1" color="grey lighten-1"
                  >mdi-map-marker-outline</v-icon
                >
                {{ coupon.address1 }}
              </span>
              <span class="redeemed-meta-item">
                <v-icon x-small class="mr-1" color="grey lighten-1"
                  >mdi-clock-outline</v-icon
                >
                {{ formatDateTime(coupon.redeemedAt) }}
              </span>
            </div>

            <div v-if="coupon.note" class="redeemed-note mt-1">
              {{ coupon.note }}
            </div>

            <v-divider
              v-if="index + 1 < filteredRedeemedCoupons.length"
              class="mt-3 mb-0"
            ></v-divider>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm dialog -->
    <v-dialog v-model="confirmDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="headline pb-2">
          Confirm Redemption
        </v-card-title>
        <v-card-text v-if="couponInfo">
          <div>
            You are about to redeem coupon
            <strong>#{{ couponInfo.coupon.id }}</strong> for:
          </div>
          <div class="confirm-account mt-2">{{ couponInfo.account.name }}</div>
          <div class="confirm-amount mt-1">
            {{ formatCurrency(couponInfo.coupon.amount) }}
          </div>
          <div class="confirm-warning mt-3">
            <v-icon small color="warning" class="mr-1"
              >mdi-alert-outline</v-icon
            >
            This action cannot be undone.
          </div>
        </v-card-text>
        <v-card-actions class="pb-4">
          <v-spacer />
          <v-btn
            dark
            color="default"
            @click="confirmDialog = false"
            :disabled="redeeming"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="redeeming"
            :disabled="redeeming"
            @click="submitRedeem"
          >
            Confirm Redeem
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success dialog -->
    <v-dialog v-model="successDialog" max-width="420" persistent>
      <v-card>
        <v-card-text class="text-xs-center pt-5 pb-4">
          <v-icon size="64" color="success">mdi-check-circle</v-icon>
          <div class="success-title mt-3">Coupon Redeemed!</div>
          <div class="success-sub mt-1" v-if="couponInfo">
            #{{ couponInfo.coupon.id }} ·
            {{ formatCurrency(couponInfo.coupon.amount) }}
          </div>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="success" @click="resetPage">Redeem Another</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Redeem error -->
    <v-alert
      :value="!!redeemError"
      type="error"
      class="mt-3"
      @input="redeemError = ''"
      style="position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); min-width: 320px; max-width: 500px; z-index: 9999;"
    >
      {{ redeemError }}
    </v-alert>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import moment from "moment";

export default {
  name: "RedeemCoupon",
  data() {
    return {
      couponIdInput: "",
      couponInputError: "",
      loadingInfo: false,
      lookupError: "",
      couponInfo: null,
      confirmDialog: false,
      redeeming: false,
      redeemError: "",
      successDialog: false,
      redeemedCoupons: [],
      loadingRedeemed: false,
      redeemedError: "",
      redeemedSearch: "",
    };
  },
  computed: {
    ...mapState("app", ["drawer"]),
    redeemedTotal() {
      return this.redeemedCoupons.reduce((sum, c) => {
        const val = Number(c.amountRedeemed);
        return sum + (Number.isNaN(val) ? 0 : val);
      }, 0);
    },
    filteredRedeemedCoupons() {
      const q = (this.redeemedSearch || "").toLowerCase().trim();
      if (!q) return this.redeemedCoupons;
      return this.redeemedCoupons.filter((c) => {
        return (
          (c.id && String(c.id).includes(q)) ||
          (c.accountID && c.accountID.toLowerCase().includes(q)) ||
          (c.accountName && c.accountName.toLowerCase().includes(q)) ||
          (c.accountNameH && c.accountNameH.includes(this.redeemedSearch.trim())) ||
          (c.address1 && c.address1.toLowerCase().includes(q)) ||
          (c.note && c.note.toLowerCase().includes(q))
        );
      });
    },
    isCouponIdValid() {
      return /^\d{6,10}$/.test(this.couponIdInput);
    },
    isAdminUser() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      return !!isAdmin && isAdmin != "false";
    },
    isDrawerVisible() {
      return this.drawer === true;
    },
  },
  mounted() {
    this.setDrawer(false);
    this.loadRedeemedByUser();
  },
  beforeDestroy() {
    this.setDrawer(null);
  },
  watch: {
    couponIdInput(value) {
      const rawValue = value == null ? "" : String(value);
      const digitsOnly = rawValue.replace(/\D/g, "").slice(0, 10);
      if (rawValue !== digitsOnly) {
        this.couponIdInput = digitsOnly;
      }
    },
  },
  methods: {
    ...mapMutations("app", ["setDrawer"]),
    getCouponValidationError(isSubmitting) {
      if (!this.couponIdInput) {
        return isSubmitting ? "Please enter a coupon number." : "";
      }
      if (!/^\d+$/.test(this.couponIdInput)) {
        return "Coupon number must contain digits only.";
      }
      if (this.couponIdInput.length < 6) {
        return "Coupon number must be at least 6 digits.";
      }
      if (this.couponIdInput.length > 10) {
        return "Coupon number can be up to 10 digits.";
      }
      return "";
    },
    onCouponInput(value) {
      const rawValue = value == null ? "" : String(value);
      const digitsOnly = rawValue.replace(/\D/g, "");
      this.couponIdInput = digitsOnly.slice(0, 10);
      this.lookupError = "";
      if (rawValue && /\D/.test(rawValue)) {
        this.couponInputError = "Coupon number must contain digits only.";
        return;
      }
      if (digitsOnly.length > 10) {
        this.couponInputError = "Coupon number can be up to 10 digits.";
        return;
      }
      this.couponInputError = "";
    },
    allowDigitsOnly(event) {
      const key = event && event.key ? event.key : "";
      if (key.length > 1) return;
      if (this.couponIdInput.length >= 10) {
        event.preventDefault();
        this.couponInputError = "Coupon number can be up to 10 digits.";
        return;
      }
      if (!/^\d$/.test(key)) {
        event.preventDefault();
        this.couponInputError = "Coupon number must contain digits only.";
      }
    },
    onCouponPaste(event) {
      const clipboardData =
        event && event.clipboardData ? event.clipboardData : null;
      const pastedText = clipboardData ? clipboardData.getData("text") : "";
      const digitsOnly = pastedText.replace(/\D/g, "");
      if (pastedText !== digitsOnly) {
        event.preventDefault();
        const nextValue = (this.couponIdInput + digitsOnly).slice(0, 10);
        this.couponIdInput = nextValue;
        this.couponInputError = "Coupon number must contain digits only.";
        return;
      }
      if (this.couponIdInput.length + digitsOnly.length > 10) {
        event.preventDefault();
        this.couponIdInput = (this.couponIdInput + digitsOnly).slice(0, 10);
        this.couponInputError = "Coupon number can be up to 10 digits.";
      }
    },
    showNavigation() {
      this.setDrawer(true);
    },
    async loadCoupon() {
      const validationError = this.getCouponValidationError(true);
      if (validationError) {
        this.couponInputError = validationError;
        this.lookupError = "";
        return;
      }
      this.couponInputError = "";
      const id = parseInt(this.couponIdInput, 10);
      this.loadingInfo = true;
      this.lookupError = "";
      this.couponInfo = null;
      try {
        const response = await this.$http.get(
          "api/campCoupons/GetRedeemInfo?id=" + id
        );
        if (response && response.error) {
          this.lookupError = response.error;
        } else if (response && response.coupon && response.account) {
          this.couponInfo = response;
        } else {
          this.lookupError = "Coupon not found.";
        }
      } catch (err) {
        if (err && err.status === 404) {
          this.lookupError = "Coupon #" + id + " was not found.";
        } else {
          this.lookupError = "Failed to load coupon info. Please try again.";
        }
      } finally {
        this.loadingInfo = false;
      }
    },
    confirmRedeem() {
      this.redeemError = "";
      this.confirmDialog = true;
    },
    async submitRedeem() {
      if (!this.couponInfo) return;
      this.redeeming = true;
      this.redeemError = "";
      try {
        await this.$http.post("api/campCoupons/Redeem", {
          couponID: this.couponInfo.coupon.id,
          amountRedeemed: this.couponInfo.coupon.amount,
        });
        this.confirmDialog = false;
        this.successDialog = true;
        this.loadRedeemedByUser();
      } catch (err) {
        this.confirmDialog = false;
        if (err && err.status === 409) {
          const conflict =
            err.data && err.data.redeemedBy
              ? "Already redeemed by " +
                err.data.redeemedBy +
                " on " +
                this.formatDateTime(err.data.redeemedAt) +
                "."
              : "This coupon was already redeemed by another user.";
          this.redeemError = conflict;
          await this.loadCoupon();
        } else {
          this.redeemError = "Failed to redeem coupon. Please try again.";
        }
      } finally {
        this.redeeming = false;
      }
    },
    async loadRedeemedByUser() {
      const user = this.$store.state.authentication && this.$store.state.authentication.user;
      if (!user) return;

      this.loadingRedeemed = true;
      this.redeemedError = "";
      try {
        const result = await this.$http.get(
          "api/campCoupons/GetRedeemedByUser?user=" + encodeURIComponent(user)
        );
        this.redeemedCoupons = Array.isArray(result) ? result : [];
      } catch (e) {
        this.redeemedError = "Failed to load redeemed coupons.";
      } finally {
        this.loadingRedeemed = false;
      }
    },
    resetPage() {
      this.successDialog = false;
      this.couponIdInput = "";
      this.couponInfo = null;
      this.lookupError = "";
      this.redeemError = "";
    },
    formatDate(value) {
      if (!value) return "—";
      return moment(value).format("MMM D, YYYY");
    },
    formatDateTime(value) {
      if (!value) return "—";
      return moment(value).format("MMM D, YYYY h:mm A");
    },
    formatCurrency(value) {
      if (value == null || value === "") return "—";
      return "$" + parseFloat(value).toFixed(2);
    },
  },
};
</script>

<style scoped>
.redeem-page {
  min-height: 100vh;
  background: #f5f6fa;
  padding-bottom: 80px;
}

.admin-nav-toggle {
  position: fixed;
  top: 12px;
  left: 16px;
  z-index: 200;
}

.admin-toggle-btn {
  background: rgba(255, 255, 255, 0.92) !important;
  border: 1px solid #e0e0e0;
  border-radius: 6px !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  text-transform: none !important;
  letter-spacing: 0;
}

.redeem-container {
  max-width: 680px;
  margin: 0 auto;
  padding: 48px 20px 40px;
}

.redeem-header {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
}

.redeem-header-icon {
  font-size: 40px !important;
  margin-right: 16px;
  opacity: 0.85;
}

.redeem-title {
  font-size: 22px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.2;
}

.redeem-subtitle {
  font-size: 13px;
  color: #888;
  margin-top: 2px;
}

.redeem-card {
  border-radius: 10px !important;
}

.lookup-card {
  background: #fff;
}

.lookup-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.coupon-input {
  font-size: 18px;
}

.coupon-input :deep(.v-input__slot) {
  min-height: 48px !important;
  padding-top: 0 !important;
}

.coupon-input :deep(input) {
  padding-top: 0 !important;
}

.load-btn {
  height: 48px !important;
  min-width: 90px !important;
  font-size: 14px;
  text-transform: none !important;
  letter-spacing: 0;
}

.coupon-input-error {
  color: #d32f2f;
  font-size: 12px;
  font-weight: 500;
}

.info-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-card {
  background: #fff;
}

.coupon-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.info-card-label {
  font-size: 11px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  display: flex;
  align-items: center;
}

.info-name {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
}

.info-name-h {
  font-size: 15px;
  color: #555;
  margin-top: 2px;
}

.info-detail {
  font-size: 13px;
  color: #777;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #999;
}

.detail-value {
  font-size: 15px;
  color: #333;
  margin-top: 2px;
  font-weight: 500;
}

.amount-value {
  font-size: 18px;
  font-weight: 700;
  color: #2e7d32;
}

.redeem-action-card {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fdf4 100%);
  border: 1px solid #c8e6c9 !important;
}

.redeem-action-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #666;
}

.redeem-amount-display {
  font-size: 32px;
  font-weight: 700;
  color: #2e7d32;
  line-height: 1.1;
  margin-top: 4px;
}

.redeem-amount-note {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.redeem-btn {
  text-transform: none !important;
  letter-spacing: 0;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px !important;
}

.confirm-account {
  font-size: 17px;
  font-weight: 600;
  color: #1a1a2e;
}

.confirm-amount {
  font-size: 22px;
  font-weight: 700;
  color: #2e7d32;
}

.confirm-warning {
  font-size: 13px;
  color: #f57c00;
  display: flex;
  align-items: center;
}

.success-title {
  font-size: 22px;
  font-weight: 700;
  color: #2e7d32;
}

.success-sub {
  font-size: 15px;
  color: #666;
}

/* Coupons redeemed section */
.redeemed-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 20px 14px;
}

.redeemed-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.redeemed-section-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.redeemed-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.redeemed-section-meta {
  font-size: 13px;
  color: #6b7280;
}

.redeemed-refresh-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 12px !important;
  min-width: 0 !important;
  height: 28px !important;
  padding: 0 10px !important;
  margin: 0 !important;
  color: #9ca3af !important;
}

.redeemed-search {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px !important;
}

.redeemed-loading {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.redeemed-error {
  font-size: 13px;
  color: #d32f2f;
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.redeemed-empty {
  font-size: 13px;
  color: #9ca3af;
  padding: 8px 0;
}

.redeemed-row {
  padding: 12px 0 8px;
}

.redeemed-row-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.redeemed-id-chip {
  display: inline-block;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.redeemed-amounts {
  display: flex;
  gap: 20px;
}

.redeemed-amt-block {
  text-align: right;
}

.redeemed-amt-label {
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.redeemed-amt-val {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.redeemed-orange .redeemed-amt-label,
.redeemed-orange .redeemed-amt-val {
  color: #d97706;
}

.redeemed-names {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
  line-height: 1.4;
}

.redeemed-account-id-plain {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1px 6px;
}

.redeemed-account-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.redeemed-name-h {
  font-size: 13px;
  color: #6b7280;
  direction: rtl;
}

.redeemed-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.redeemed-meta-item {
  font-size: 12px;
  color: #9ca3af;
  display: flex;
  align-items: center;
}

.redeemed-note {
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
  background: #fafafa;
  border-left: 2px solid #e5e7eb;
  padding: 3px 8px;
  border-radius: 0 4px 4px 0;
}
</style>
